import { Flex, useWindowSize } from '@solinkcorp/solink-components';
import classNames from 'classnames';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import useLocale from 'src/hooks/useLocale';
import { Button } from '../Button';
import { type Device } from '../types/device.types';
import { type Event, type Info } from '../types/event.types';
import {
  formatDay,
  formatTimeOfDay,
  getStartAndEndTime,
  getTimezoneName,
} from '../utils/helperFunctions';
import './SubHeader.sass';

interface SubHeaderProps {
  event: Event | null;
  status?: 'OPEN' | 'CLOSED' | undefined;
  info?: Info | null;
  device: Device | null;
  sharePage?: boolean;
  alarmPage?: boolean;
  tokenExpired?: boolean;
  eventUrl?: string;
  error?: string;
  locationName?: string;
  displayLiveButton?: boolean;
  isPlayingLive?: boolean;
  toggleLive?: (playLive?: boolean) => void;
}

// Component
const SubHeader: React.FC<SubHeaderProps> = ({
  event,
  status,
  info,
  device,
  sharePage,
  alarmPage,
  tokenExpired,
  eventUrl,
  error,
  locationName,
  isPlayingLive,
  displayLiveButton,
  toggleLive,
}) => {
  const { width } = useWindowSize();
  const locale = useLocale();

  if (error && error === locale.spa.requestedResourceIsUnavailable)
    return <div className="subheader"></div>;

  if (!event || !device)
    return (
      <div className="subheader">
        <Flex className="subheader-container">
          <div className="entry-left">
            <div className="upper-text">
              <label>
                <Skeleton
                  className="skeleton-label-text"
                  baseColor="#3f3f3f"
                  highlightColor="#3e3e3e"
                />
              </label>
            </div>
            <div className="lower-text text">
              <Skeleton
                className="skeleton-info-text"
                baseColor="#3f3f3f"
                highlightColor="#3e3e3e"
              />
            </div>
          </div>
          <div className="entry-right">
            <Flex justifyContent="flex-end" alignItems="center" style={{ height: '100%' }}>
              <div className="fake-button"></div>
            </Flex>
          </div>
        </Flex>
      </div>
    );
  let abbreviate = false;
  if (width && width < 480) {
    abbreviate = true;
  }

  const { startTime, endTime } = getStartAndEndTime(event, sharePage);
  const { timezoneId } = device;
  const start = formatTimeOfDay({ time: startTime, timezoneId });
  const end = formatTimeOfDay({ time: endTime, timezoneId });
  const date = formatDay({ time: startTime, timezoneId, abbreviate });
  const timezoneName = getTimezoneName(timezoneId, startTime);

  const label = sharePage ? locationName?.toUpperCase() : locale.spa.alarmStatus;
  const subheaderClass = classNames('subheader', {
    'alarm-page': alarmPage && status === 'OPEN' && !tokenExpired,
  });

  const updatedEventUrl = updateEventUrlHost(eventUrl ?? '');

  const handleLiveClick = () => toggleLive?.();

  const liveButtonClass = classNames('live-button', { live: isPlayingLive });

  return (
    <div className={subheaderClass}>
      <Flex className="subheader-container">
        <div className="entry-left">
          <div className="upper-text">
            <label data-testid="subheader-location">
              {label}
              {alarmPage && (
                <span className="alarm-status text">
                  {status === 'OPEN' ? locale.spa.open : locale.spa.closed}
                </span>
              )}
            </label>
            {!!displayLiveButton && (
              <button className={liveButtonClass} onClick={handleLiveClick}>
                {locale.general.live}
              </button>
            )}
          </div>
          <div className="lower-text">
            <span className="date" data-testid="subheader-date">
              {date}
            </span>
            <span className="comma">, </span>
            <span className="time" data-testid="subheader-time">
              {status === 'OPEN' ? start : `${start} - ${end}`} {timezoneName}
            </span>
          </div>
        </div>
        <div className="entry-right">
          <Flex
            justifyContent="flex-end"
            alignItems="center"
            style={{ height: '100%' }}
            className="subheader-button-wrapper"
          >
            <Button
              sharePage={sharePage}
              alarmPage={alarmPage}
              event={event}
              info={info}
              eventUrl={updatedEventUrl}
              tokenExpired={tokenExpired}
              status={status}
            />
          </Flex>
        </div>
      </Flex>
    </div>
  );
};

export default SubHeader;

function updateEventUrlHost(url: string) {
  if (url === '') {
    return '';
  }
  const newUrl = new URL(url);

  newUrl.hostname = location.hostname;

  if (location.port) {
    newUrl.port = location.port;
  }

  return newUrl.toString();
}
